import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  useCallback,
  useDeferredValue,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { Link } from 'react-router';
import { ApiError } from '@crave/api-client';
import {
  formatNumber,
  getUnit,
  roundForLabel,
  scaleNutrients,
  toBaseQuantity,
  type BaseUnit,
  type NutrientKey,
  type Nutrients,
} from '@crave/costing';
import { Button, Card, Field, NutritionFactsLabel, SelectField, allergenLabels } from '@crave/ui';
import { api } from '../api.ts';
import { ConfirmDelete } from '../components/ConfirmDelete.tsx';
import { HistoryLink } from '../components/HistoryLink.tsx';
import {
  MeasureIn,
  formatQty,
  useMeasureStyle,
  type MeasureStyle,
} from '../components/MeasureIn.tsx';
import {
  UnitSelect,
  defaultUnitValue,
  splitUnitValue,
  unavailableUnitsHint,
  unitValue,
  type UnitIngredient,
} from '../components/UnitSelect.tsx';
import { Notice, Section, Table, errorText, growHeight, wholeLines } from '../components/ui.tsx';
import {
  amountText,
  centsText,
  changeMode,
  defaultChoice,
  expandableNames,
  highlightParts,
  isPizza,
  makesText,
  nutritionRows,
  parseAmount,
  pdfCostLines,
  pdfIngredients,
  plainName,
  queryWords,
  scaleFactor,
  scaleModes,
  scaleNote,
  scaleVersion,
  searchRecipes,
  textMatches,
  versionText,
  yieldBase,
  type KindFilter,
  type ScaleChoice,
  type ScaleContext,
  type ScaleResult,
  type ScaledVersion,
} from '../lib/recipeScale.ts';
import { useStaff } from '../lib/staff.tsx';
import type * as PdfMaker from '../pdf/index.ts';
import type { RecipePdfInput, RecipePdfRecipe } from '../pdf/types.ts';

interface Line {
  id?: string;
  ingredient_id: string;
  ingredient?: string;
  base_unit?: string;
  /** Normalized to the ingredient's base unit (g, ml or each). */
  quantity: number;
  unit: string;
  /** The line as it was typed: a quantity of a unit or of one of the ingredient's packages. */
  entry_quantity?: number | null;
  entry_unit?: string | null;
  entry_package_id?: string | null;
  entry_label?: string | null;
  waste_factor: number;
  is_optional: boolean;
  is_removable: boolean;
  modifier_behavior: string;
  cost_cents?: number | null;
}
interface Nutrition {
  complete: boolean;
  /** Names of the ingredients that still need nutrition values or a weight. */
  missing: string[];
  /** Grams in one yield unit (one pizza, one dough ball, one gram or ml). */
  grams: number | null;
  per_yield: Nutrients | null;
  per: 'g' | 'ml' | 'each';
}
interface Version {
  id: string;
  version: number;
  status: string;
  notes: string | null;
  published_at?: string | null;
  unit_cost_cents: number | null;
  has_complete_cost: boolean;
  allergens?: string[] | null;
  nutrition?: Nutrition | null;
  lines: Line[] | null;
}
interface Recipe {
  id: string;
  code: string;
  name: string;
  kind: string;
  menu_item_id?: string | null;
  menu_item_name: string | null;
  servings_per_item?: number | null;
  produces_ingredient_id?: string | null;
  produces_name: string | null;
  yield_qty: number;
  yield_unit: string;
  can_delete?: boolean;
  delete_blocked_reason?: string | null;
  uses?: Record<string, number>;
  /** Method steps, in order. Recipe-level: the same for every version. */
  steps?: string[] | null;
  versions: Version[] | null;
}
interface Ingredient extends UnitIngredient {
  id: string;
  name: string;
  base_unit: BaseUnit;
  is_prep: boolean;
  is_active?: boolean;
  allergens: string[];
  yield_pct: number;
  cost_cents_per_base: number | null;
}

const ingredientHref = (id: string) => `/ingredients?${new URLSearchParams({ id }).toString()}`;

/** What one cost or nutrition amount is for: a pizza, a dough ball, 100 g of sauce. */
function yieldBasis(r: Recipe): { factor: number; label: string } {
  if (r.yield_unit === 'g' || r.yield_unit === 'ml')
    return { factor: 100, label: `100 ${r.yield_unit}` };
  if (r.yield_unit === 'each') {
    if (r.kind === 'MENU_ITEM') return { factor: 1, label: 'pizza' };
    return { factor: 1, label: r.produces_name ? plainName(r.produces_name) : 'piece' };
  }
  return { factor: 1, label: getUnit(r.yield_unit)?.abbr ?? r.yield_unit };
}

function costText(r: Recipe, v: Version): string {
  if (!v.has_complete_cost || v.unit_cost_cents === null) return 'Food cost: not complete yet';
  const basis = yieldBasis(r);
  return `Food cost: ${centsText(v.unit_cost_cents * basis.factor)} per ${basis.label}`;
}

const STATUS_WORD: Record<string, string> = {
  published: 'live',
  draft: 'draft',
  retired: 'retired',
};

function subtitle(r: Recipe, produced: Ingredient | undefined, style: MeasureStyle): string {
  if (r.kind !== 'PREP') {
    const menu =
      r.menu_item_name && r.menu_item_name !== r.name
        ? `Menu item: ${r.menu_item_name}.`
        : 'Menu item.';
    const slices = r.servings_per_item ? ` Cut into ${r.servings_per_item} slices.` : '';
    return `${menu}${slices}`;
  }
  const what = r.produces_name ?? 'a prep ingredient';
  if (r.yield_unit === 'each')
    return `Prep recipe. Makes ${formatNumber(r.yield_qty, 2)} × ${what}.`;
  let amount = `${formatNumber(r.yield_qty, 2)} ${getUnit(r.yield_unit)?.abbr ?? r.yield_unit}`;
  if (produced) {
    try {
      amount = formatQty(toBaseQuantity(r.yield_qty, r.yield_unit, produced), produced, style);
    } catch {
      // Keep the yield as the recipe states it.
    }
  }
  return `Prep recipe. Makes ${amount} of ${what}.`;
}

/* ---------- Nutrition ---------- */

const SUMMARY: readonly (readonly [NutrientKey, string, '' | 'g' | 'mg'])[] = [
  ['calories', 'Calories', ''],
  ['fat_g', 'Fat', 'g'],
  ['sodium_mg', 'Sodium', 'mg'],
  ['carbohydrate_g', 'Carbs', 'g'],
  ['protein_g', 'Protein', 'g'],
];

/** Rounded the way the Nutrition Facts label rounds, so the summary and the label agree. */
function nutrientText(key: NutrientKey, v: number, unit: '' | 'g' | 'mg'): string {
  const rounded = roundForLabel(key, v);
  const text = rounded.value.toLocaleString('en-US', { maximumFractionDigits: 1 });
  const shown = unit ? `${text} ${unit}` : text;
  return rounded.lessThan ? `Under ${shown}` : shown;
}

interface NutritionBasis {
  /** "per pizza", "per dough ball", "per 100 g" */
  per: string;
  amounts: Nutrients;
  label: {
    perServing: Nutrients;
    servingSize: string;
    servings: number | null;
    dual: boolean;
    servingColumn: string;
    containerColumn: string;
  };
}

function nutritionBasis(r: Recipe, n: Nutrition): NutritionBasis | null {
  const perYield = n.per_yield;
  if (!n.complete || !perYield) return null;
  const weight = (g: number | null) =>
    g !== null && Number.isFinite(g) && g > 0 ? ` (${Math.round(g)}g)` : '';
  if (n.per === 'each') {
    if (r.kind === 'MENU_ITEM') {
      const servings = r.servings_per_item && r.servings_per_item > 0 ? r.servings_per_item : null;
      if (servings) {
        return {
          per: 'per pizza',
          amounts: perYield,
          label: {
            perServing: scaleNutrients(perYield, 1 / servings),
            servingSize: `1 slice${weight(n.grams === null ? null : n.grams / servings)}`,
            servings,
            dual: true,
            servingColumn: 'Per slice',
            containerColumn: 'Per pizza',
          },
        };
      }
      return {
        per: 'per pizza',
        amounts: perYield,
        label: {
          perServing: perYield,
          servingSize: `1 pizza${weight(n.grams)}`,
          servings: null,
          dual: false,
          servingColumn: 'Per pizza',
          containerColumn: 'Per pizza',
        },
      };
    }
    const thing = r.produces_name ? plainName(r.produces_name) : 'piece';
    return {
      per: `per ${thing}`,
      amounts: perYield,
      label: {
        perServing: perYield,
        servingSize: `1 ${thing}${weight(n.grams)}`,
        servings: null,
        dual: false,
        servingColumn: `Per ${thing}`,
        containerColumn: `Per ${thing}`,
      },
    };
  }
  const per100 = scaleNutrients(perYield, 100);
  return {
    per: `per 100 ${n.per}`,
    amounts: per100,
    label: {
      perServing: per100,
      servingSize:
        n.per === 'g' ? '100g' : `100 ml${weight(n.grams === null ? null : n.grams * 100)}`,
      servings: null,
      dual: false,
      servingColumn: `Per 100 ${n.per}`,
      containerColumn: `Per 100 ${n.per}`,
    },
  };
}

/** "A, B and C" with each part allowed to be a link. */
function joinWords(parts: ReactNode[]): ReactNode[] {
  const out: ReactNode[] = [];
  parts.forEach((p, i) => {
    if (i > 0) out.push(i === parts.length - 1 ? ' and ' : ', ');
    out.push(p);
  });
  return out;
}

/** "dough ball, pizza sauce and hot honey" */
function joinPlain(names: string[]): string {
  const words = [...new Set(names.map(plainName))];
  return words.length <= 1
    ? (words[0] ?? '')
    : `${words.slice(0, -1).join(', ')} and ${words[words.length - 1]}`;
}

function AllergenChips({ codes }: { codes: string[] | null | undefined }) {
  const labels = allergenLabels(codes);
  return (
    <div className="rc-fact">
      <span className="rc-fact-title">Allergens</span>
      {labels.length ? (
        <ul className="rc-chips" aria-label="Contains">
          {labels.map((l) => (
            <li key={l} className="rc-chip">
              {l}
            </li>
          ))}
        </ul>
      ) : (
        <span className="rc-muted">No major allergens</span>
      )}
    </div>
  );
}

function VersionFacts({
  recipe,
  version,
  ingredientsByName,
}: {
  recipe: Recipe;
  version: Version;
  ingredientsByName: Map<string, Ingredient>;
}) {
  const n = version.nutrition ?? null;
  const basis = n ? nutritionBasis(recipe, n) : null;
  const missing = n?.missing ?? [];
  return (
    <>
      <div className="rc-facts">
        <AllergenChips codes={version.allergens} />
        {basis ? (
          <div className="rc-fact">
            <span className="rc-fact-title">Nutrition {basis.per}</span>
            <dl className="rc-stats">
              {SUMMARY.map(([key, label, unit]) => {
                const v = basis.amounts[key];
                if (typeof v !== 'number') return null;
                return (
                  <div key={key} className="rc-stat">
                    <dt>{label}</dt>
                    <dd>{nutrientText(key, v, unit)}</dd>
                  </div>
                );
              })}
            </dl>
          </div>
        ) : (
          <div className="rc-fact">
            <span className="rc-fact-title">Nutrition</span>
            <p className="rc-incomplete">
              Nutrition incomplete: add values for{' '}
              {missing.length
                ? joinWords(
                    missing.map((name) => {
                      const ing = ingredientsByName.get(name);
                      return ing ? (
                        <Link key={name} className="rc-link crave-hit" to={ingredientHref(ing.id)}>
                          {name}
                        </Link>
                      ) : (
                        <strong key={name}>{name}</strong>
                      );
                    }),
                  )
                : 'its ingredients'}{' '}
              on the{' '}
              <Link className="rc-link crave-hit" to="/ingredients">
                Ingredients page
              </Link>
              .
            </p>
          </div>
        )}
      </div>
      {basis && (
        <details className="rc-details">
          <summary>Nutrition facts</summary>
          <NutritionFactsLabel
            perServing={basis.label.perServing}
            servingSize={basis.label.servingSize}
            servingsPerContainer={basis.label.servings}
            showPerContainer={basis.label.dual}
            perContainer={basis.label.dual ? basis.amounts : null}
            servingColumnLabel={basis.label.servingColumn}
            containerColumnLabel={basis.label.containerColumn}
            headingLevel={4}
          />
        </details>
      )}
    </>
  );
}

/* ---------- Search highlight ---------- */

function Highlight({ text, words }: { text: string; words: readonly string[] }) {
  if (words.length === 0) return <>{text}</>;
  return (
    <>
      {highlightParts(text, words).map((p, i) =>
        p.hit ? (
          <mark key={i} className="rc-mark">
            {p.text}
          </mark>
        ) : (
          <span key={i}>{p.text}</span>
        ),
      )}
    </>
  );
}

const NO_INSIDE: ReadonlyMap<string, string[]> = new Map();

/** Ingredient lines at the chosen amount. A line matching the search is highlighted. */
function LinesTable({
  scaled,
  showSaved,
  words,
  inside,
}: {
  scaled: ScaledVersion;
  showSaved: boolean;
  words: readonly string[];
  /** Kitchen-made line -> matching ingredients inside it. */
  inside: ReadonlyMap<string, string[]>;
}) {
  return (
    <div className="rc-lines">
      <Table
        head={[
          'Ingredient',
          'Amount',
          ...(showSaved ? ['Saved recipe'] : []),
          'Waste',
          'Removable',
          'Cost',
        ]}
        rowKeys={scaled.lines.map((l) => l.key)}
        empty="No ingredients yet."
        rows={scaled.lines.map((l) => {
          const within = l.expandedFrom === null ? inside.get(l.ingredientId) : undefined;
          const hit = (words.length > 0 && textMatches(l.name, words)) || within !== undefined;
          return [
            <div key="name" className={hit ? 'rc-name rc-hit' : 'rc-name'}>
              <Link className="rc-link crave-hit" to={ingredientHref(l.ingredientId)}>
                <Highlight text={l.name} words={words} />
              </Link>
              {l.expandedFrom && (
                <span className="rc-from">From the {plainName(l.expandedFrom)} recipe</span>
              )}
              {within && (
                <span className="rc-from">
                  Made with <Highlight text={within.join(', ')} words={words} />
                </span>
              )}
            </div>,
            <strong key="amount">{l.amount}</strong>,
            ...(showSaved ? [l.savedAmount] : []),
            l.waste,
            l.removable ? 'Yes' : 'No',
            centsText(l.costCents),
          ];
        })}
      />
    </div>
  );
}

function VersionBlock({
  recipe,
  version,
  scaled,
  showSaved,
  words,
  inside,
  ingredientsByName,
  actions,
}: {
  recipe: Recipe;
  version: Version;
  scaled: ScaledVersion;
  showSaved: boolean;
  words: readonly string[];
  inside: ReadonlyMap<string, string[]>;
  ingredientsByName: Map<string, Ingredient>;
  actions?: ReactNode;
}) {
  const live = version.status === 'published';
  const published = live ? versionText(version).replace(/^Version \d+, /, '') : null;
  return (
    <section className="rc-version" aria-label={`${recipe.name}, version ${version.version}`}>
      <div className="rc-version-head">
        <h4 className="rc-h4">Version {version.version}</h4>
        <span className={live ? 'rc-badge rc-badge-live' : 'rc-badge rc-badge-draft'}>
          {live ? 'Live' : 'Draft, not live yet'}
        </span>
        {published && published !== 'live' && (
          <span className="rc-muted">{published.charAt(0).toUpperCase() + published.slice(1)}</span>
        )}
        <HistoryLink
          entityType="recipe_versions"
          entityId={version.id}
          name={`${recipe.name}, version ${version.version}`}
        />
        {actions && <div className="rc-version-actions">{actions}</div>}
      </div>
      <VersionFacts recipe={recipe} version={version} ingredientsByName={ingredientsByName} />
      <LinesTable scaled={scaled} showSaved={showSaved} words={words} inside={inside} />
    </section>
  );
}

/* ---------- How much are you making? ---------- */

function ScalePanel({
  recipe,
  produced,
  choice,
  result,
  costCents,
  expandable,
  style,
  onChange,
}: {
  recipe: Recipe;
  produced: Ingredient | undefined;
  choice: ScaleChoice;
  result: ScaleResult;
  costCents: number | null;
  expandable: string[];
  style: MeasureStyle;
  onChange: (c: ScaleChoice) => void;
}) {
  const ids = useId();
  const modes = scaleModes(recipe, produced, choice.amount, choice.mode);
  const saved = result.ok && Math.abs(result.factor - 1) < 1e-9;
  const stepper = choice.mode === 'count' || choice.mode === 'batch';
  const n = parseAmount(choice.amount);
  const step = (d: number) => {
    const next = (n > 0 ? n : 0) + d;
    if (next > 0) onChange({ ...choice, amount: amountText(next) });
  };

  let summary: string;
  if (result.ok) {
    const note = scaleNote(recipe, result.factor);
    summary = `${makesText(recipe, produced, result.madeBase, style)}.`;
    if (note) summary += ` ${note.charAt(0).toUpperCase()}${note.slice(1)}.`;
    if (costCents !== null) summary += ` Food cost: ${centsText(costCents)}.`;
  } else {
    summary = `${result.problem} Showing the saved recipe for now.`;
  }

  return (
    <div className="rc-scale" role="group" aria-labelledby={`${ids}-label`}>
      <div className="rc-scale-row">
        <label id={`${ids}-label`} htmlFor={`${ids}-amount`} className="rc-scale-label">
          How much are you making?
        </label>
        <div className="rc-scale-controls">
          {stepper && (
            <button
              type="button"
              className="rc-step"
              aria-label="One less"
              disabled={!(n > 1)}
              onClick={() => step(-1)}
            >
              −
            </button>
          )}
          <input
            id={`${ids}-amount`}
            className="rc-input rc-scale-amount"
            inputMode="decimal"
            autoComplete="off"
            enterKeyHint="done"
            value={choice.amount}
            aria-invalid={result.ok ? undefined : true}
            aria-describedby={`${ids}-makes`}
            onFocus={(e) => e.currentTarget.select()}
            onChange={(e) => onChange({ ...choice, amount: e.target.value })}
          />
          {stepper && (
            <button type="button" className="rc-step" aria-label="One more" onClick={() => step(1)}>
              +
            </button>
          )}
          {modes.length > 1 ? (
            <select
              className="rc-input rc-scale-mode"
              aria-label="Counted in"
              value={choice.mode}
              onChange={(e) => onChange(changeMode(recipe, choice, e.target.value))}
            >
              {modes.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </select>
          ) : (
            <span className="rc-scale-unit">{modes[0]?.label}</span>
          )}
        </div>
        {!saved && (
          <Button
            variant="ghost"
            className="rc-scale-reset"
            style={{ padding: '0 12px', textDecoration: 'underline', textUnderlineOffset: 3 }}
            onClick={() =>
              onChange({
                ...defaultChoice(recipe),
                expand: choice.expand,
                showSaved: choice.showSaved,
              })
            }
          >
            Back to the saved recipe
          </Button>
        )}
      </div>
      <p
        id={`${ids}-makes`}
        className={result.ok ? 'rc-scale-makes' : 'rc-problem'}
        aria-live="polite"
      >
        {summary}
      </p>
      <div className="rc-scale-toggles">
        <label className="rc-check">
          <input
            type="checkbox"
            checked={choice.showSaved}
            onChange={(e) => onChange({ ...choice, showSaved: e.target.checked })}
          />
          <span>Show saved recipe amounts</span>
        </label>
        {expandable.length > 0 && (
          <label className="rc-check">
            <input
              type="checkbox"
              checked={choice.expand}
              onChange={(e) => onChange({ ...choice, expand: e.target.checked })}
            />
            <span>Break {joinPlain(expandable)} down into raw ingredients</span>
          </label>
        )}
      </div>
    </div>
  );
}

/* ---------- Method steps ---------- */

let stepCounter = 0;
const newStepKey = () => `step-${++stepCounter}`;
/** The API's limits (PUT /v1/admin/recipes/:id/steps). */
const MAX_STEPS = 60;
const MAX_STEP_LENGTH = 1000;

/**
 * About three lines to start with, and about a dozen at most; past that the box scrolls. The largest
 * size is trimmed to whole lines when the box is measured, so the bottom never shows half a line.
 */
const STEP_MIN_HEIGHT = 96;
const STEP_MAX_HEIGHT = 320;

/**
 * One step box. It grows with what is typed and shrinks back again, so a long step is never shown
 * as a half-cut line on a phone. The height is set before the browser paints, so it does not jump
 * while typing.
 */
function StepText({
  id,
  value,
  onChange,
}: {
  id: string;
  value: string;
  onChange: (text: string) => void;
}) {
  const ref = useRef<HTMLTextAreaElement>(null);
  const fit = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    // Back to nothing first, so the box can shrink as well as grow.
    el.style.height = 'auto';
    // scrollHeight leaves the borders out, and a border-box height has to carry them.
    const borders = el.offsetHeight - el.clientHeight;
    // A box stopped at a plain number of pixels draws whatever line lands on the edge cut in half, so
    // the largest size comes down to the last line that fits whole. Read from the page, not guessed,
    // because bigger text on the phone means taller lines. A box with more in it than it can show
    // keeps the padding blank at one end and runs the writing under the other, so one padding counts
    // against the writing, not two.
    const style = getComputedStyle(el);
    const padding = Math.max(parseFloat(style.paddingTop), parseFloat(style.paddingBottom));
    const max = wholeLines(STEP_MAX_HEIGHT, borders + padding, parseFloat(style.lineHeight));
    el.style.height = `${growHeight(el.scrollHeight, borders, STEP_MIN_HEIGHT, max)}px`;
  }, []);

  useLayoutEffect(() => {
    fit();
  }, [value, fit]);

  // A narrower box wraps the same words onto more lines, so it has to be measured again.
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    let width = el.clientWidth;
    const observer = new ResizeObserver(() => {
      if (el.clientWidth === width) return;
      width = el.clientWidth;
      fit();
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [fit]);

  return (
    <textarea
      ref={ref}
      id={id}
      className="rc-input rc-step-text"
      rows={3}
      maxLength={MAX_STEP_LENGTH}
      value={value}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

function StepsSection({
  recipe,
  words,
  onSaved,
}: {
  recipe: Recipe;
  words: readonly string[];
  onSaved: (text: string) => void;
}) {
  const { can } = useStaff();
  const qc = useQueryClient();
  const ids = useId();
  const steps = recipe.steps ?? [];
  /** The steps being edited, or null while just showing them. */
  const [items, setItems] = useState<{ key: string; text: string }[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const focusNext = useRef<string | null>(null);

  useEffect(() => {
    if (!focusNext.current) return;
    document.getElementById(focusNext.current)?.focus();
    focusNext.current = null;
  });

  const cleaned = (items ?? []).map((i) => i.text.trim()).filter(Boolean);
  const dirty = items !== null && JSON.stringify(cleaned) !== JSON.stringify(steps);
  const textId = (key: string) => `${ids}-text-${key}`;
  const upId = (key: string) => `${ids}-up-${key}`;
  const downId = (key: string) => `${ids}-down-${key}`;

  const save = useMutation({
    mutationFn: async (list: string[]) =>
      (await api.put<{ steps?: string[] }>(`/admin/recipes/${recipe.id}/steps`, { steps: list }))
        .data,
    onSuccess: async (data, list) => {
      const savedSteps = data?.steps ?? list;
      setItems(null);
      setError(null);
      onSaved(
        savedSteps.length === 0
          ? `Method steps removed from ${recipe.name}.`
          : `Method saved for ${recipe.name}: ${savedSteps.length === 1 ? '1 step' : `${savedSteps.length} steps`}.`,
      );
      await qc.invalidateQueries({ queryKey: ['recipes'] });
    },
    onError: (e) =>
      setError(`Nothing was saved. ${errorText(e, "Couldn't save the steps. Try again.")}`),
  });

  const start = () => {
    const list = (steps.length ? steps : ['']).map((text) => ({ key: newStepKey(), text }));
    setError(null);
    setItems(list);
    focusNext.current = textId(list[0]!.key);
  };

  const move = (i: number, d: -1 | 1) =>
    setItems((list) => {
      if (!list) return list;
      const j = i + d;
      if (j < 0 || j >= list.length) return list;
      const next = [...list];
      const [moved] = next.splice(i, 1);
      next.splice(j, 0, moved!);
      // Keep focus on the moved step; its button at the end of the list is disabled, so use the other.
      const key = moved!.key;
      focusNext.current =
        j === 0 ? downId(key) : j === next.length - 1 ? upId(key) : d < 0 ? upId(key) : downId(key);
      return next;
    });

  const remove = (i: number) =>
    setItems((list) => {
      if (!list) return list;
      const next = list.filter((_, k) => k !== i);
      const after = next[i] ?? next[i - 1];
      focusNext.current = after ? textId(after.key) : `${ids}-add`;
      return next;
    });

  const add = () =>
    setItems((list) => {
      if ((list?.length ?? 0) >= MAX_STEPS) return list;
      const item = { key: newStepKey(), text: '' };
      focusNext.current = textId(item.key);
      return [...(list ?? []), item];
    });

  return (
    <section className="rc-steps" aria-labelledby={`${ids}-title`}>
      <div className="rc-steps-head">
        <h4 id={`${ids}-title`} className="rc-h4">
          {items === null ? 'Method' : 'Editing the method'}
        </h4>
        {items === null && can('recipes.manage') && (
          <Button variant="secondary" onClick={start}>
            {steps.length ? 'Edit steps' : 'Add steps'}
          </Button>
        )}
      </div>
      {items === null ? (
        steps.length ? (
          <ol className="rc-steps-list">
            {steps.map((s, i) => (
              <li key={i}>
                <Highlight text={s} words={words} />
              </li>
            ))}
          </ol>
        ) : (
          <p className="rc-muted" style={{ margin: 0 }}>
            No method steps yet.
          </p>
        )
      ) : (
        <>
          <p className="rc-editor-note">
            One step per box, in the order the kitchen does them. Steps belong to the whole recipe,
            not a version, so saving changes them right away. Empty boxes are left out. Up to{' '}
            {MAX_STEPS} steps.
          </p>
          <ol className="rc-step-edit">
            {items.map((it, i) => (
              <li key={it.key} className="rc-step-item">
                <label htmlFor={textId(it.key)} className="rc-step-label">
                  Step {i + 1}
                </label>
                <StepText
                  id={textId(it.key)}
                  value={it.text}
                  onChange={(text) =>
                    setItems((list) =>
                      list ? list.map((x) => (x.key === it.key ? { ...x, text } : x)) : list,
                    )
                  }
                />
                <div className="rc-step-tools">
                  <Button
                    id={upId(it.key)}
                    variant="secondary"
                    style={{ padding: '0 12px' }}
                    disabled={i === 0}
                    aria-label={`Move step ${i + 1} up`}
                    onClick={() => move(i, -1)}
                  >
                    Move up
                  </Button>
                  <Button
                    id={downId(it.key)}
                    variant="secondary"
                    style={{ padding: '0 12px' }}
                    disabled={i === items.length - 1}
                    aria-label={`Move step ${i + 1} down`}
                    onClick={() => move(i, 1)}
                  >
                    Move down
                  </Button>
                  <Button
                    variant="ghost"
                    style={{ padding: '0 12px', color: 'var(--crave-red)' }}
                    aria-label={`Remove step ${i + 1}`}
                    onClick={() => remove(i)}
                  >
                    Remove
                  </Button>
                </div>
              </li>
            ))}
          </ol>
          <div className="rc-actions">
            <Button
              id={`${ids}-add`}
              variant="secondary"
              disabled={items.length >= MAX_STEPS}
              onClick={add}
            >
              + Add step
            </Button>
            <Button onClick={() => save.mutate(cleaned)} disabled={save.isPending || !dirty}>
              {save.isPending ? 'Saving…' : 'Save steps'}
            </Button>
            <Button
              variant="ghost"
              disabled={save.isPending}
              onClick={() => {
                if (
                  !dirty ||
                  confirm('Close without saving? Your changes to the steps will be lost.')
                ) {
                  setItems(null);
                  setError(null);
                }
              }}
            >
              Cancel
            </Button>
          </div>
          {error && <Notice kind="error">{error}</Notice>}
        </>
      )}
    </section>
  );
}

/* ---------- Draft editor ---------- */

/**
 * Draft line editor: a header row over aligned columns when the card is wide, and each line as its
 * own box of labelled fields when it is narrow (phones). The per-field labels stay in the DOM for
 * screen readers and are only visually hidden while the header row is showing. Under each line a
 * message row shows the amount in the chosen measure, a missing setup, or the API's error.
 */
const RECIPES_CSS = `
.rc-card-head { display: flex; justify-content: space-between; flex-wrap: wrap; gap: 8px 16px; align-items: flex-start; }
.rc-card-head > div:first-child { min-width: 0; flex: 1 1 260px; }
.rc-title { margin: 0; font-size: 20px; line-height: 1.25; overflow-wrap: anywhere; }
.rc-title:focus { outline: none; }
.rc-title:focus-visible { outline: 3px solid var(--crave-red); outline-offset: 2px; }
.rc-sub { margin: 2px 0 0; color: var(--ink-soft); font-size: 14px; }
.rc-head-side { display: flex; flex-wrap: wrap; gap: 8px 12px; align-items: center; }
.rc-cost { font-weight: 700; }
.rc-muted { color: var(--ink-soft); font-size: 14px; }
.rc-link { color: inherit; text-decoration: underline; text-underline-offset: 3px; }
.rc-version { display: grid; gap: 12px; margin-top: 16px; padding-top: 16px; border-top: 1px solid var(--line); min-width: 0; }
.rc-version-head { display: flex; flex-wrap: wrap; align-items: center; gap: 6px 12px; }
.rc-h4 { margin: 0; font-size: 17px; line-height: 1.3; }
.rc-badge { display: inline-block; padding: 2px 10px; border-radius: 999px; font-size: 13px; font-weight: 700; line-height: 1.5; }
.rc-badge-live { background: #e3f1e8; color: #1f5c39; }
.rc-badge-draft { background: #fbefd9; color: #6e4608; }
.rc-version-actions { display: flex; flex-wrap: wrap; gap: 8px; margin-left: auto; }
.rc-facts { display: grid; grid-template-columns: repeat(auto-fit, minmax(min(100%, 240px), 1fr)); gap: 12px 24px; align-items: start; }
.rc-facts > .rc-fact:last-child:not(:first-child) { grid-column: span 2; }
@container (max-width: 520px) { .rc-facts > .rc-fact:last-child:not(:first-child) { grid-column: auto; } }
.rc-fact { display: grid; gap: 6px; min-width: 0; align-content: start; }
.rc-fact-title { font-size: 14px; font-weight: 700; color: var(--ink-soft); }
.rc-chips { display: flex; flex-wrap: wrap; gap: 6px; margin: 0; padding: 0; list-style: none; }
.rc-chip { padding: 2px 10px; border-radius: 999px; border: 1px solid var(--line); background: var(--mist); font-size: 14px; font-weight: 600; }
.rc-stats { display: flex; flex-wrap: wrap; gap: 8px; margin: 0; }
.rc-stat { min-width: 82px; padding: 5px 10px; border: 1px solid var(--line); border-radius: var(--radius-sm); background: var(--paper); }
.rc-stat dt { font-size: 13px; color: var(--ink-soft); line-height: 1.3; }
.rc-stat dd { margin: 0; font-weight: 700; font-size: 15px; white-space: nowrap; }
.rc-incomplete { margin: 0; font-size: 15px; }
.rc-details > summary { cursor: pointer; font-weight: 600; min-height: var(--touch); display: flex; align-items: center; gap: 8px;
  width: fit-content; list-style: none; text-decoration: underline; text-underline-offset: 3px; }
.rc-details > summary::-webkit-details-marker { display: none; }
.rc-details > summary::before { content: ''; width: 8px; height: 8px; border-right: 2px solid currentColor; border-bottom: 2px solid currentColor;
  transform: rotate(-45deg); transition: transform 0.15s; flex-shrink: 0; }
.rc-details[open] > summary::before { transform: rotate(45deg); }
h2 + .crave-measure { margin-left: auto; }
@media (max-width: 720px) { h2 + .crave-measure { flex: 1 1 100%; margin-left: 0; } }
.rc-foot { display: flex; flex-wrap: wrap; align-items: center; gap: 4px 16px; margin-top: 16px; padding-top: 8px; border-top: 1px solid var(--line); font-size: 14px; color: var(--ink-soft); }
.rc-foot-actions { display: flex; flex-wrap: wrap; align-items: center; gap: 4px 12px; margin-left: auto; }
.rc-blocked { display: grid; gap: 8px; margin-top: 12px; padding: 12px 14px; border: 2px solid var(--warn); border-radius: var(--radius-sm); font-size: 15px; }
.rc-actions { display: flex; flex-wrap: wrap; gap: 8px; align-items: center; }
.rc-actions .rc-push { margin-left: auto; }
.rc-problem { margin: 0; font-size: 15px; color: var(--crave-red); }
.rc-editing { border-top: 3px solid var(--crave-red); }
.rc-editor-note { margin: 0; font-size: 14px; color: var(--ink-soft); }
.rc-button-link { display: inline-flex; align-items: center; min-height: var(--touch); padding: 0 16px; border-radius: var(--radius-sm);
  border: 2px solid var(--line); background: var(--paper); color: var(--ink); font-weight: 600; text-decoration: none; }
@media (max-width: 560px) {
  .rc-actions > button, .rc-actions > a { flex: 1 1 auto; justify-content: center; }
  .rc-actions .rc-push { margin-left: 0; }
  .rc-version-actions { margin-left: 0; width: 100%; }
  .rc-version-actions > button { flex: 1 1 auto; }
  .rc-foot-actions { margin-left: 0; }
}

.rc-input { min-height: var(--touch); min-width: 0; padding: 0 12px; border-radius: var(--radius-sm); border: 2px solid var(--line);
  background: var(--paper); color: var(--ink); font: inherit; font-size: 16px; }
.rc-input[aria-invalid='true'] { border-color: var(--crave-red); }
.rc-input:focus-visible, .rc-step:focus-visible, .rc-filter:focus-visible { outline: 3px solid var(--crave-red); outline-offset: 1px; }
.rc-check { display: inline-flex; align-items: center; gap: 10px; min-height: var(--touch); cursor: pointer; font-size: 15px; line-height: 1.3; }
.rc-check input { width: 20px; height: 20px; margin: 0; flex-shrink: 0; accent-color: var(--crave-red); }
.rc-sr { position: absolute; width: 1px; height: 1px; overflow: hidden; clip-path: inset(50%); white-space: nowrap; }

.rc-find { position: sticky; top: 0; z-index: 6; display: grid; gap: 8px; margin-inline: -8px; padding: 10px 8px;
  background: var(--ivory); border-bottom: 1px solid var(--line); }
.rc-find-row { display: flex; gap: 8px; align-items: center; }
.rc-search { flex: 1 1 auto; width: 100%; }
.rc-filters { display: flex; flex-wrap: wrap; gap: 8px; align-items: center; }
.rc-filter { min-height: var(--touch); padding: 0 16px; border-radius: 999px; border: 2px solid var(--line); background: var(--paper);
  color: var(--ink); font: inherit; font-size: 15px; font-weight: 600; cursor: pointer; }
.rc-filter[aria-pressed='true'] { background: var(--ink); border-color: var(--ink); color: #fff; }
.rc-count { margin-left: auto; font-size: 14px; color: var(--ink-soft); }
.rc-card { scroll-margin-top: 140px; }
@media (max-width: 760px) {
  .rc-find { position: static; margin-inline: 0; padding: 0; border-bottom: 0; background: transparent; }
  .rc-card { scroll-margin-top: 96px; }
}
.rc-print { display: flex; flex-wrap: wrap; align-items: center; gap: 4px 16px; }
.rc-empty { display: grid; gap: 8px; justify-items: start; padding: 20px; border: 2px dashed var(--line); border-radius: var(--radius);
  background: var(--paper); font-size: 15px; }
/* The page has its own 44px Clear button; the browser's small one would be a second, tiny target. */
.rc-search::-webkit-search-cancel-button { -webkit-appearance: none; appearance: none; display: none; }
.rc-mark { background: #ffe08a; color: inherit; border-radius: 3px; padding: 0 1px; }
/* A grid child sizes to its content unless told otherwise; this lets the table scroll inside the card. */
.rc-lines { min-width: 0; }
.rc-lines tr:has(.rc-hit) > td { background: #fff7db; }
.rc-name { display: grid; gap: 2px; }
.rc-from { font-size: 13px; color: var(--ink-soft); }

.rc-scale { display: grid; gap: 8px; margin-top: 14px; padding: 12px 14px; border: 1px solid var(--line); border-radius: var(--radius-sm); background: var(--mist); }
.rc-scale-row { display: flex; flex-wrap: wrap; align-items: center; gap: 8px 12px; }
.rc-scale-label { font-weight: 700; font-size: 15px; }
.rc-scale-controls { display: flex; flex-wrap: wrap; align-items: center; gap: 6px; min-width: 0; }
.rc-scale-amount { width: 5.5em; text-align: center; }
.rc-scale-mode { max-width: 100%; }
/* Safari ignores min-height on a native select, so a select gets a fixed 44px height. */
select.rc-input { height: var(--touch); }
.rc-scale-unit { font-weight: 600; padding: 0 4px; }
.rc-step { width: var(--touch); height: var(--touch); border-radius: var(--radius-sm); border: 2px solid var(--line); background: var(--paper);
  color: var(--ink); font: inherit; font-size: 22px; font-weight: 700; line-height: 1; cursor: pointer; }
.rc-step:disabled { opacity: 0.45; cursor: default; }
.rc-scale-makes { margin: 0; font-size: 15px; font-weight: 600; }
.rc-scale-toggles { display: flex; flex-wrap: wrap; gap: 0 24px; }
@media (max-width: 560px) { .rc-scale-label { flex: 1 1 100%; } }

.rc-steps { display: grid; gap: 10px; margin-top: 16px; padding-top: 16px; border-top: 1px solid var(--line); min-width: 0; }
.rc-steps-head { display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between; gap: 8px 12px; }
.rc-steps-list { display: grid; gap: 8px; margin: 0; padding-left: 1.6em; font-size: 15px; line-height: 1.5; }
.rc-steps-list li { padding-left: 4px; overflow-wrap: anywhere; }
.rc-step-edit { display: grid; gap: 12px; margin: 0; padding: 0; list-style: none; }
.rc-step-item { display: grid; gap: 6px; padding: 12px; border: 1px solid var(--line); border-radius: var(--radius-sm); background: var(--paper); }
.rc-step-label { font-size: 14px; font-weight: 700; }
/* The box sets its own height from what is in it, so dragging it by hand would only be undone. */
.rc-step-text { width: 100%; padding: 10px 12px; line-height: 1.45; resize: none; overflow-y: auto; }
.rc-step-tools { display: flex; flex-wrap: wrap; gap: 6px; }
@media (max-width: 560px) { .rc-step-tools > button { flex: 1 1 auto; } }

.rl-lines { container-type: inline-size; display: grid; gap: 10px; }
.rl-line {
  display: grid;
  grid-template-columns: minmax(0, 1.9fr) minmax(0, 0.8fr) minmax(0, 1.9fr) minmax(0, 0.7fr) 124px 44px;
  grid-template-areas: 'ing qty unit waste rem rm' 'msg msg msg msg msg msg';
  column-gap: 8px;
  row-gap: 4px;
  align-items: center;
  padding-bottom: 10px;
  border-bottom: 1px solid var(--line);
}
.rl-line.rl-head { font-size: 14px; font-weight: 600; line-height: 1.3; color: var(--ink-soft); padding-bottom: 0; border-bottom: 0; }
.rl-line > .crave-field { position: relative; grid-template-rows: auto; row-gap: 0; }
.rl-line > .crave-field > .crave-field-label {
  position: absolute; width: 1px; height: 1px; overflow: hidden; clip-path: inset(50%); white-space: nowrap;
}
.rl-ing { grid-area: ing; }
.rl-qty { grid-area: qty; }
.rl-unit { grid-area: unit; }
.rl-waste { grid-area: waste; }
.rl-rem { grid-area: rem; }
.rl-rm { grid-area: rm; }
.rl-msg { grid-area: msg; display: flex; flex-wrap: wrap; gap: 2px 14px; font-size: 14px; line-height: 1.4; color: var(--ink-soft); min-width: 0; }
.rl-msg:empty { display: none; }
.rl-err { color: var(--crave-red); font-weight: 600; }
@container (max-width: 599px) {
  .rl-line.rl-head { display: none; }
  .rl-line {
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) 44px;
    grid-template-areas: 'ing ing rm' 'qty unit unit' 'waste rem rem' 'msg msg msg';
    row-gap: 12px;
    align-items: end;
    padding: 12px;
    border: 1px solid var(--line);
    border-radius: var(--radius-sm);
  }
  .rl-line > .crave-field { grid-template-rows: auto auto; row-gap: 6px; }
  .rl-line > .crave-field > .crave-field-label {
    position: static; width: auto; height: auto; overflow: visible; clip-path: none; white-space: normal;
  }
}
`;

interface DraftLine {
  key: string;
  ingredient_id: string;
  /** Typed amount, kept as text so "0." and "1.5" can be typed. */
  qty: string;
  /** Picker value: 'unit:<code>' or 'package:<id>'. */
  unit: string;
  /** Percent, as typed. */
  waste: string;
  is_optional: boolean;
  is_removable: boolean;
  modifier_behavior: string;
}

let lineCounter = 0;
const newLineKey = () => `line-${++lineCounter}`;
const numText = (n: number) => String(Math.round(n * 10000) / 10000);
const parseNum = (s: string) => {
  const t = s.trim().replace(',', '.');
  return t === '' ? Number.NaN : Number(t);
};
const wastePct = (s: string) => (s.trim() === '' ? 0 : parseNum(s));

/** Open a saved line as it was entered (28 oz can, 2 cups), falling back to its base amount. */
function toDraftLine(l: Line): DraftLine {
  const entered =
    (Boolean(l.entry_unit) || Boolean(l.entry_package_id)) &&
    l.entry_quantity !== null &&
    l.entry_quantity !== undefined;
  return {
    key: newLineKey(),
    ingredient_id: l.ingredient_id,
    qty: numText(entered ? Number(l.entry_quantity) : l.quantity),
    unit: entered ? unitValue(l.entry_unit, l.entry_package_id) : unitValue(l.unit),
    waste: numText(l.waste_factor * 100),
    is_optional: l.is_optional,
    is_removable: l.is_removable,
    modifier_behavior: l.modifier_behavior,
  };
}

const linesBody = (lines: DraftLine[]) => ({
  lines: lines.map((l) => ({
    ingredient_id: l.ingredient_id,
    quantity: parseNum(l.qty),
    ...splitUnitValue(l.unit),
    waste_factor: wastePct(l.waste) / 100,
    is_optional: l.is_optional,
    is_removable: l.is_removable,
    modifier_behavior: l.modifier_behavior,
  })),
});

/** The same rules the API applies to draft lines, so Save and Publish never send bad lines. */
function linesProblem(lines: DraftLine[], nameOf: (id: string) => string): string | null {
  if (lines.length === 0) return 'Add at least one ingredient.';
  const seen = new Set<string>();
  for (const [i, l] of lines.entries()) {
    const n = `Line ${i + 1}`;
    if (!l.ingredient_id) return `${n}: choose an ingredient.`;
    if (!l.unit) return `${n}: choose a unit.`;
    if (!(parseNum(l.qty) > 0)) return `${n}: the amount must be more than 0.`;
    const w = wastePct(l.waste);
    if (!(w >= 0 && w <= 90)) return `${n}: waste must be between 0% and 90%.`;
    if (seen.has(l.ingredient_id))
      return `${nameOf(l.ingredient_id)} is on more than one line. Combine them into one line.`;
    seen.add(l.ingredient_id);
  }
  return null;
}

/** The typed amount in the chosen measure ("Equals 127 g"), or null when it adds nothing. */
function lineEquivalent(
  l: DraftLine,
  ing: Ingredient | undefined,
  style: MeasureStyle,
): string | null {
  if (!ing) return null;
  const n = parseNum(l.qty);
  if (!(n > 0)) return null;
  const fields = splitUnitValue(l.unit) as { unit?: string; package_id?: string };
  let base: number;
  try {
    if (fields.package_id) base = toBaseQuantity(n, { packageId: fields.package_id }, ing);
    else if (fields.unit) base = toBaseQuantity(n, fields.unit, ing);
    else return null;
  } catch {
    return null;
  }
  const pkg = fields.package_id ? ing.packages?.find((p) => p.id === fields.package_id) : undefined;
  const typed = formatQty(base, ing, 'entered', {
    entered: {
      quantity: n,
      unit: fields.unit ?? null,
      package_id: pkg?.id ?? null,
      label: pkg?.label ?? null,
    },
  });
  const shown = formatQty(base, ing, style === 'entered' ? 'metric' : style);
  return shown === typed ? null : shown;
}

/** Explain a missing conversion only when the chosen measure needs it. */
function setupHint(ing: Ingredient | undefined, style: MeasureStyle): string | null {
  if (!ing) return null;
  const needs =
    (ing.base_unit === 'g' && style === 'us_volume') ||
    (ing.base_unit === 'ml' && style === 'us_weight') ||
    (ing.base_unit === 'each' &&
      (style === 'metric' || style === 'us_weight' || style === 'imperial'));
  return needs ? unavailableUnitsHint(ing) : null;
}

/** Publish failed after the lines were saved: the draft holds the edits but is not live. */
class SavedButNotPublished extends Error {
  readonly inner: unknown;
  constructor(inner: unknown) {
    super('saved but not published');
    this.inner = inner;
  }
}

function DraftEditor({
  recipe,
  version,
  ingredients,
  ingredientsById,
  ingredientsByName,
  onClose,
  onDiscard,
  onPublished,
}: {
  recipe: Recipe;
  version: Version;
  ingredients: Ingredient[];
  ingredientsById: Map<string, Ingredient>;
  ingredientsByName: Map<string, Ingredient>;
  onClose: () => void;
  onDiscard: () => void;
  onPublished: () => void;
}) {
  const qc = useQueryClient();
  const { can } = useStaff();
  const [style] = useMeasureStyle();
  const ids = useId();
  const ref = useRef<HTMLElement>(null);
  const [lines, setLines] = useState<DraftLine[]>(() => (version.lines ?? []).map(toDraftLine));
  const [dirty, setDirty] = useState(false);
  const [lineErrors, setLineErrors] = useState<Record<string, string>>({});
  const [msg, setMsg] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);

  useEffect(() => {
    ref.current?.scrollIntoView({ block: 'start', behavior: 'smooth' });
  }, []);

  const nameOf = (id: string) => ingredientsById.get(id)?.name ?? 'An ingredient';
  const problem = linesProblem(lines, nameOf);

  const options = useMemo(
    () =>
      ingredients
        .filter(
          (i) =>
            i.id !== recipe.produces_ingredient_id &&
            (i.is_active !== false || lines.some((l) => l.ingredient_id === i.id)),
        )
        .map((i) => [i.id, i.is_active === false ? `${i.name} (inactive)` : i.name] as const),
    [ingredients, recipe.produces_ingredient_id, lines],
  );

  const update = (key: string, patch: Partial<DraftLine>) => {
    setLines((ls) => ls.map((l) => (l.key === key ? { ...l, ...patch } : l)));
    setDirty(true);
    setMsg(null);
    setLineErrors((errs) => {
      if (!(key in errs)) return errs;
      const next = { ...errs };
      delete next[key];
      return next;
    });
  };

  const showError = (e: unknown, prefix: string) => {
    const inner = e instanceof SavedButNotPublished ? e.inner : e;
    const details = inner instanceof ApiError ? inner.details : undefined;
    const ingredientId =
      details && typeof details === 'object'
        ? (details as { ingredient_id?: unknown }).ingredient_id
        : undefined;
    if (typeof ingredientId === 'string') {
      const hits = lines.filter((l) => l.ingredient_id === ingredientId);
      if (hits.length) {
        const text = errorText(inner);
        setLineErrors(Object.fromEntries(hits.map((l) => [l.key, text])));
        setMsg({ kind: 'error', text: `${prefix} Fix the line marked in red.` });
        return;
      }
    }
    setMsg({ kind: 'error', text: `${prefix} ${errorText(inner)}` });
  };

  const save = useMutation({
    mutationFn: (ls: DraftLine[]) =>
      api.put(`/admin/recipes/versions/${version.id}/lines`, linesBody(ls)),
    onSuccess: async () => {
      setDirty(false);
      setLineErrors({});
      setMsg({ kind: 'ok', text: 'Draft saved. Allergens and nutrition below now match it.' });
      await qc.invalidateQueries({ queryKey: ['recipes'] });
    },
    onError: (e) => showError(e, 'Nothing was saved.'),
  });

  const publish = useMutation({
    // Save what is on screen first, so the published version is exactly what the owner edited.
    mutationFn: async (ls: DraftLine[]) => {
      await api.put(`/admin/recipes/versions/${version.id}/lines`, linesBody(ls));
      try {
        await api.post(`/admin/recipes/versions/${version.id}/publish`);
      } catch (e) {
        throw new SavedButNotPublished(e);
      }
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['recipes'] });
      onPublished();
    },
    onError: (e) => {
      if (e instanceof SavedButNotPublished) {
        setDirty(false);
        void qc.invalidateQueries({ queryKey: ['recipes'] });
        showError(e, 'Draft saved, but not published.');
      } else {
        showError(e, 'Nothing was saved or published.');
      }
    },
  });

  const busy = save.isPending || publish.isPending;

  return (
    <section
      ref={ref}
      className="rc-version rc-editing"
      aria-labelledby={`${ids}-title`}
      style={{ scrollMarginTop: 16 }}
    >
      <div className="rc-version-head">
        <h4 id={`${ids}-title`} className="rc-h4">
          Editing version {version.version}
        </h4>
        <span className="rc-badge rc-badge-draft">Draft, not live yet</span>
        <HistoryLink
          entityType="recipe_versions"
          entityId={version.id}
          name={`${recipe.name}, version ${version.version}`}
        />
      </div>
      <p className="rc-editor-note">
        Type amounts the way you measure them: grams, ounces, cups or a package. Save keeps the
        draft; Publish makes it the recipe for new orders.
      </p>
      <MeasureIn label="Show amounts in" />
      <div className="rl-lines">
        <div className="rl-line rl-head" aria-hidden="true">
          <span className="rl-ing">Ingredient</span>
          <span className="rl-qty">Amount</span>
          <span className="rl-unit">Unit</span>
          <span className="rl-waste">Waste %</span>
        </div>
        {lines.map((l, i) => {
          const ing = ingredientsById.get(l.ingredient_id);
          const err = lineErrors[l.key];
          const equivalent = err ? null : lineEquivalent(l, ing, style);
          const hint = err ? null : setupHint(ing, style);
          const msgId = `${ids}-msg-${l.key}`;
          const described = err || equivalent || hint ? msgId : undefined;
          return (
            <div key={l.key} className="rl-line" role="group" aria-label={`Line ${i + 1}`}>
              <SelectField
                className="rl-ing"
                label="Ingredient"
                value={l.ingredient_id}
                onChange={(e) => {
                  const id = e.target.value;
                  update(l.key, {
                    ingredient_id: id,
                    unit: defaultUnitValue(ingredientsById.get(id)),
                  });
                }}
                options={options}
                placeholder="Choose…"
                aria-invalid={err ? true : undefined}
                aria-describedby={described}
              />
              <Field
                className="rl-qty"
                label="Amount"
                inputMode="decimal"
                autoComplete="off"
                value={l.qty}
                onChange={(e) => update(l.key, { qty: e.target.value })}
                aria-invalid={err ? true : undefined}
                aria-describedby={described}
              />
              <UnitSelect
                className="rl-unit"
                label="Unit"
                ingredient={ing ?? null}
                value={l.unit}
                onValueChange={(v) => update(l.key, { unit: v })}
                placeholder="Choose…"
                showUnavailableHint={false}
                aria-invalid={err ? true : undefined}
                aria-describedby={described}
              />
              <Field
                className="rl-waste"
                label="Waste %"
                inputMode="decimal"
                autoComplete="off"
                value={l.waste}
                onChange={(e) => update(l.key, { waste: e.target.value })}
              />
              <label
                className="rl-rem"
                style={{
                  display: 'flex',
                  gap: 8,
                  alignItems: 'center',
                  minHeight: 'var(--touch)',
                  paddingInline: 4,
                  cursor: 'pointer',
                }}
              >
                <input
                  type="checkbox"
                  checked={l.is_removable}
                  style={{ width: 18, height: 18, margin: 0, flexShrink: 0 }}
                  onChange={(e) =>
                    update(l.key, {
                      is_removable: e.target.checked,
                      modifier_behavior: e.target.checked ? 'REMOVABLE' : 'NONE',
                    })
                  }
                />
                <span style={{ lineHeight: 1.3 }}>Removable</span>
              </label>
              <Button
                className="rl-rm"
                variant="ghost"
                aria-label={`Remove line ${i + 1}${ing ? `, ${ing.name}` : ''}`}
                style={{ width: 44, padding: 0, fontSize: 22 }}
                onClick={() => {
                  setLines((ls) => ls.filter((x) => x.key !== l.key));
                  setDirty(true);
                }}
              >
                ×
              </Button>
              <div id={msgId} className="rl-msg">
                {err && (
                  <span className="rl-err" role="alert">
                    {err}
                  </span>
                )}
                {equivalent && <span>Equals {equivalent}</span>}
                {hint && ing && (
                  <span>
                    {hint}{' '}
                    <Link className="rc-link crave-hit" to={ingredientHref(ing.id)}>
                      Open {ing.name}
                    </Link>
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
      <div className="rc-actions">
        <Button
          variant="secondary"
          onClick={() => {
            setLines((ls) => [
              ...ls,
              {
                key: newLineKey(),
                ingredient_id: '',
                qty: '1',
                unit: '',
                waste: '0',
                is_optional: false,
                is_removable: false,
                modifier_behavior: 'NONE',
              },
            ]);
            setDirty(true);
          }}
        >
          + Add ingredient
        </Button>
        <Button
          variant="secondary"
          onClick={() => save.mutate(lines)}
          disabled={busy || problem !== null}
        >
          {save.isPending ? 'Saving…' : 'Save draft'}
        </Button>
        <Button
          onClick={() => {
            if (
              confirm('Save these lines and publish? This becomes the recipe for all new orders.')
            )
              publish.mutate(lines);
          }}
          disabled={busy || problem !== null}
        >
          {publish.isPending ? 'Publishing…' : 'Publish'}
        </Button>
        <Button
          variant="ghost"
          disabled={busy}
          onClick={() => {
            if (!dirty || confirm('Close without saving? Your changes to this draft will be lost.'))
              onClose();
          }}
        >
          Close
        </Button>
        {can('recipes.manage') && (
          <Button className="rc-push" variant="danger" disabled={busy} onClick={onDiscard}>
            Discard draft
          </Button>
        )}
      </div>
      {problem && <p className="rc-problem">{problem}</p>}
      {msg && <Notice kind={msg.kind}>{msg.text}</Notice>}
      <p className="rc-editor-note">
        {dirty
          ? 'Allergens and nutrition as of the last save:'
          : 'Allergens and nutrition for this draft:'}
      </p>
      <VersionFacts recipe={recipe} version={version} ingredientsByName={ingredientsByName} />
    </section>
  );
}

/* ---------- Page ---------- */

type PdfMode = 'print' | 'download';

/** The PDF maker's own short sentence is shown as is; anything technical becomes a plain one. */
function pdfErrorText(e: unknown): string {
  const m = e instanceof Error ? e.message.trim() : '';
  const plain = /^[A-Z][^{}<>_=]{0,160}[.!]$/.test(m) && !/\b(undefined|null|TypeError)\b/.test(m);
  return plain ? m : "Couldn't make the PDF. Try again.";
}

type PdfModule = typeof PdfMaker;
/** The PDF maker once loaded. It is small; the renderer and fonts load only when a PDF is made. */
let pdfModule: PdfModule | null = null;

const warmPdf = () => {
  if (pdfModule) return;
  import('../pdf/index.ts')
    .then((m) => {
      pdfModule = m;
    })
    .catch(() => undefined);
};

export function Recipes() {
  const qc = useQueryClient();
  const { can } = useStaff();
  const [style] = useMeasureStyle();
  const ids = useId();
  const q = useQuery({
    queryKey: ['recipes'],
    queryFn: async () => (await api.get<{ items: Recipe[] }>('/admin/recipes')).data.items,
  });
  const ings = useQuery({
    queryKey: ['ingredients'],
    queryFn: async () =>
      (
        await api.get<{
          items: Ingredient[];
          units: { code: string; kind: string; name: string }[];
        }>('/admin/recipes/ingredients')
      ).data,
  });
  const [msg, setMsg] = useState<{
    recipeId: string | null;
    kind: 'ok' | 'error';
    text: string;
  } | null>(null);
  /** The draft version open in the editor. */
  const [editingId, setEditingId] = useState<string | null>(null);
  const [discardTarget, setDiscardTarget] = useState<{ recipe: Recipe; version: Version } | null>(
    null,
  );
  const [deleteTarget, setDeleteTarget] = useState<Recipe | null>(null);
  const [blockedId, setBlockedId] = useState<string | null>(null);

  // Search, quick filters and amounts live on this screen only. Nothing is saved.
  const searchRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState('');
  const [kind, setKind] = useState<KindFilter>('all');
  const [hasDraft, setHasDraft] = useState(false);
  const [choices, setChoices] = useState<Record<string, ScaleChoice>>({});
  const [includeCosts, setIncludeCosts] = useState(false);
  const [includeNutrition, setIncludeNutrition] = useState(false);
  const [printing, setPrinting] = useState<{ key: string; mode: PdfMode } | null>(null);
  const [printError, setPrintError] = useState<{ key: string; text: string } | null>(null);
  const deferredQuery = useDeferredValue(query);

  const recipes = useMemo(() => q.data ?? [], [q.data]);
  const ingredients = useMemo(() => ings.data?.items ?? [], [ings.data]);
  const ingredientsById = useMemo(() => new Map(ingredients.map((i) => [i.id, i])), [ingredients]);
  const ingredientsByName = useMemo(
    () => new Map(ingredients.map((i) => [i.name, i])),
    [ingredients],
  );
  const ctx = useMemo<ScaleContext>(
    () => ({ style, ingredientsById, recipes }),
    [style, ingredientsById, recipes],
  );
  const words = useMemo(() => queryWords(deferredQuery), [deferredQuery]);
  const hits = useMemo(
    () => searchRecipes(recipes, { query: deferredQuery, kind, hasDraft }, ctx),
    [recipes, deferredQuery, kind, hasDraft, ctx],
  );
  const hitById = useMemo(() => new Map(hits.map((h) => [h.recipe.id, h])), [hits]);
  const filtering = words.length > 0 || kind !== 'all' || hasDraft;

  // Load the small PDF maker with the page, so Print can open its tab during the tap.
  useEffect(() => {
    warmPdf();
  }, []);

  // "/" jumps to the search box, like most sites.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== '/' || e.metaKey || e.ctrlKey || e.altKey) return;
      const t = e.target as HTMLElement | null;
      if (t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName))) return;
      e.preventDefault();
      searchRef.current?.focus();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  const refresh = () => qc.invalidateQueries({ queryKey: ['recipes'] });

  const draft = useMutation({
    mutationFn: async (r: Recipe) =>
      (
        await api.post<{ id: string; changed_lines?: unknown[] }>(
          `/admin/recipes/${r.id}/versions`,
          {},
        )
      ).data,
    onSuccess: (data, r) => {
      void refresh();
      setEditingId(data.id);
      const changed = data.changed_lines?.length ?? 0;
      setMsg({
        recipeId: r.id,
        kind: 'ok',
        text:
          'Draft created. Change it below, then publish.' +
          (changed
            ? ` ${changed === 1 ? 'One amount was' : `${changed} amounts were`} worked out again with today's ingredient settings; check before publishing.`
            : ''),
      });
    },
    onError: (e, r) => setMsg({ recipeId: r.id, kind: 'error', text: errorText(e) }),
  });

  const discard = async (recipe: Recipe, version: Version) => {
    await api.delete(`/admin/recipes/versions/${version.id}`);
    if (editingId === version.id) setEditingId(null);
    setBlockedId(null);
    const live = (recipe.versions ?? []).some((v) => v.status === 'published');
    setMsg({
      recipeId: recipe.id,
      kind: 'ok',
      text: `Draft version ${version.version} discarded.${live ? ' The live version is unchanged.' : ''}`,
    });
    await refresh();
  };

  /** A recipe at the amount chosen on its card (or the saved recipe while that amount is unusable). */
  const scaleOf = (r: Recipe) => {
    const choice = choices[r.id] ?? defaultChoice(r);
    const result = scaleFactor(r, choice);
    const scale = result.ok ? result : { factor: 1, madeBase: yieldBase(r) };
    return { choice, result, scale };
  };

  const pdfRecipe = (r: Recipe): RecipePdfRecipe | null => {
    const versions = r.versions ?? [];
    const v =
      versions.find((x) => x.status === 'published') ?? versions.find((x) => x.status === 'draft');
    if (!v) return null;
    const { choice, scale } = scaleOf(r);
    const expand = choice.expand && expandableNames(v, ctx).length > 0;
    const scaled = scaleVersion(r, v, scale, expand, ctx);
    const produced = r.produces_ingredient_id
      ? ingredientsById.get(r.produces_ingredient_id)
      : undefined;
    const basis = v.nutrition ? nutritionBasis(r, v.nutrition) : null;
    return {
      name: r.name,
      kind: isPizza(r) ? 'pizza' : 'kitchen-made',
      makesText: makesText(r, produced, scale.madeBase, style),
      scaleNote: scaleNote(r, scale.factor),
      versionText: versionText(v),
      ingredients: pdfIngredients(scaled.lines),
      steps: r.steps ?? [],
      allergens: allergenLabels(v.allergens),
      costText: includeCosts
        ? scaled.costCents === null
          ? 'Food cost: not complete yet'
          : `Food cost for this amount: ${centsText(scaled.costCents)}`
        : null,
      costLines: includeCosts ? pdfCostLines(scaled.lines) : null,
      nutrition:
        includeNutrition && basis
          ? {
              servingText: `Serving size: ${basis.label.servingSize}`,
              rows: nutritionRows(basis.label.perServing),
            }
          : null,
    };
  };

  const openPdf = async (list: Recipe[], mode: PdfMode, key: string) => {
    const items = list.map(pdfRecipe).filter((x): x is RecipePdfRecipe => x !== null);
    if (items.length === 0) {
      setPrintError({
        key,
        text: 'There is nothing to print yet. Add ingredients to a recipe first.',
      });
      return;
    }
    setPrintError(null);
    setPrinting({ key, mode });
    try {
      const input: RecipePdfInput = {
        recipes: items,
        booklet: key === 'all',
        includeCosts,
        includeNutrition,
        printedAt: new Date().toISOString(),
      };
      // Call the PDF maker within the tap when it is already loaded: on iPhone it opens the tab the
      // PDF shows in, and Safari blocks a tab opened after waiting.
      const pdf = pdfModule ?? (pdfModule = await import('../pdf/index.ts'));
      await pdf.openRecipePdf(input, { mode });
    } catch (e) {
      setPrintError({ key, text: pdfErrorText(e) });
    } finally {
      setPrinting(null);
    }
  };

  const clearSearch = () => {
    setQuery('');
    setKind('all');
    setHasDraft(false);
    searchRef.current?.focus();
  };

  const goToFirstMatch = () => {
    const first = hits[0];
    if (!first) return;
    const title = document.getElementById(`${ids}-title-${first.recipe.id}`);
    title?.scrollIntoView({ block: 'start', behavior: 'smooth' });
    title?.focus({ preventScroll: true });
  };

  const deleteDraft = deleteTarget?.versions?.find((v) => v.status === 'draft');
  const deleteVersions = deleteTarget?.versions ?? [];
  const deleteLines = deleteVersions.reduce((n, v) => n + (v.lines?.length ?? 0), 0);
  const listId = `${ids}-list`;
  const countId = `${ids}-count`;

  return (
    <Section title="Recipes" right={<MeasureIn />}>
      <style>{RECIPES_CSS}</style>
      {msg && msg.recipeId === null && <Notice kind={msg.kind}>{msg.text}</Notice>}
      <p style={{ margin: 0, fontSize: 15, color: 'var(--ink-soft)' }}>
        Every pizza and every prep batch has a recipe with versions. The live version never changes:
        to adjust a recipe, start a draft, edit it and publish. Costs update as receipts are posted.
        Changing how much you are making only changes what you see and print. The saved recipe stays
        the same.
      </p>

      <div className="rc-find" role="search">
        <div className="rc-find-row">
          <label htmlFor={`${ids}-search`} className="rc-sr">
            Search recipes and ingredients
          </label>
          <input
            ref={searchRef}
            id={`${ids}-search`}
            type="search"
            className="rc-input rc-search"
            placeholder="Find a recipe or ingredient"
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
            enterKeyHint="search"
            aria-controls={listId}
            aria-describedby={countId}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Escape' && query) {
                e.preventDefault();
                setQuery('');
              } else if (e.key === 'Enter') {
                e.preventDefault();
                goToFirstMatch();
              }
            }}
          />
          {query && (
            <Button
              variant="ghost"
              style={{ padding: '0 12px' }}
              onClick={() => {
                setQuery('');
                searchRef.current?.focus();
              }}
            >
              Clear
            </Button>
          )}
        </div>
        <div className="rc-filters" role="group" aria-label="Show only">
          {(
            [
              ['pizzas', 'Pizzas'],
              ['kitchen', 'Kitchen-made'],
            ] as const
          ).map(([k, label]) => (
            <button
              key={k}
              type="button"
              className="rc-filter"
              aria-pressed={kind === k}
              onClick={() => setKind(kind === k ? 'all' : k)}
            >
              {label}
            </button>
          ))}
          <button
            type="button"
            className="rc-filter"
            aria-pressed={hasDraft}
            onClick={() => setHasDraft(!hasDraft)}
          >
            Has a draft
          </button>
          <span id={countId} className="rc-count" aria-live="polite">
            {q.isSuccess && filtering
              ? `${hits.length === 1 ? '1 recipe' : `${hits.length} recipes`} of ${recipes.length}`
              : ''}
          </span>
        </div>
      </div>

      <div className="rc-print">
        <Button
          variant="secondary"
          disabled={printing !== null || recipes.length === 0}
          onPointerEnter={warmPdf}
          onFocus={warmPdf}
          onClick={() => void openPdf(recipes, 'print', 'all')}
        >
          {printing?.key === 'all' ? 'Making the PDF…' : 'Print all recipes'}
        </Button>
        <label className="rc-check">
          <input
            type="checkbox"
            checked={includeCosts}
            onChange={(e) => setIncludeCosts(e.target.checked)}
          />
          <span>Include costs</span>
        </label>
        <label className="rc-check">
          <input
            type="checkbox"
            checked={includeNutrition}
            onChange={(e) => setIncludeNutrition(e.target.checked)}
          />
          <span>Include nutrition</span>
        </label>
      </div>
      {printError?.key === 'all' && <Notice kind="error">{printError.text}</Notice>}

      {q.isLoading && <p className="rc-muted">Loading recipes…</p>}
      {q.error && (
        <Notice kind="error">{errorText(q.error, "Couldn't load the recipes. Try again.")}</Notice>
      )}
      {q.isSuccess && recipes.length === 0 && <p className="rc-muted">No recipes yet.</p>}
      {q.isSuccess && recipes.length > 0 && hits.length === 0 && (
        <div className="rc-empty" role="status">
          <strong>
            {words.length
              ? `No recipes match "${deferredQuery.trim()}"${kind !== 'all' || hasDraft ? ' with these filters' : ''}.`
              : 'No recipes match these filters.'}
          </strong>
          <span>
            Check the spelling or try part of a word, like "moz". Search looks at recipe names and
            ingredients.
          </span>
          <Button variant="secondary" onClick={clearSearch}>
            Show all recipes
          </Button>
        </div>
      )}

      <div id={listId} style={{ display: 'grid', gap: 12 }}>
        {recipes.map((r) => {
          const versions = r.versions ?? [];
          const pub = versions.find((v) => v.status === 'published');
          const dr = versions.find((v) => v.status === 'draft');
          const editing = dr !== undefined && editingId === dr.id;
          const blocked = blockedId === r.id;
          const hit = hitById.get(r.id);
          // Hidden, not removed, so an open editor keeps what was typed while searching.
          const shown = hit !== undefined || editing;
          const produced = r.produces_ingredient_id
            ? ingredientsById.get(r.produces_ingredient_id)
            : undefined;
          const main = pub ?? dr;
          const { choice, result, scale } = scaleOf(r);
          const expandable = expandableNames(main, ctx);
          const expand = choice.expand && expandable.length > 0;
          const scaledPub = pub ? scaleVersion(r, pub, scale, expand, ctx) : null;
          const scaledDraft = dr ? scaleVersion(r, dr, scale, expand, ctx) : null;
          const inside = hit?.inside ?? NO_INSIDE;
          const setChoice = (c: ScaleChoice) => setChoices((all) => ({ ...all, [r.id]: c }));
          const pdfBusy = printing?.key === r.id;
          const canPrint = main !== undefined && result.ok && printing === null;
          return (
            <Card key={r.id} className="rc-card" hidden={!shown} style={{ minWidth: 0 }}>
              <div className="rc-card-head">
                <div>
                  <h3 id={`${ids}-title-${r.id}`} className="rc-title" tabIndex={-1}>
                    <Highlight text={r.name} words={words} />
                  </h3>
                  <p className="rc-sub">{subtitle(r, produced, style)}</p>
                </div>
                <div className="rc-head-side">
                  <span className="rc-cost">{pub ? costText(r, pub) : 'Not published yet'}</span>
                  {main && (
                    <>
                      <Button
                        variant="secondary"
                        disabled={!canPrint}
                        aria-label={`Print ${r.name}`}
                        onPointerEnter={warmPdf}
                        onFocus={warmPdf}
                        onClick={() => void openPdf([r], 'print', r.id)}
                      >
                        {pdfBusy && printing?.mode === 'print' ? 'Making the PDF…' : 'Print'}
                      </Button>
                      <Button
                        variant="secondary"
                        disabled={!canPrint}
                        aria-label={`Save ${r.name} as a PDF`}
                        onPointerEnter={warmPdf}
                        onFocus={warmPdf}
                        onClick={() => void openPdf([r], 'download', r.id)}
                      >
                        {pdfBusy && printing?.mode === 'download' ? 'Making the PDF…' : 'Save PDF'}
                      </Button>
                    </>
                  )}
                  {!dr && (
                    <Button
                      variant="secondary"
                      onClick={() => draft.mutate(r)}
                      disabled={draft.isPending}
                    >
                      {draft.isPending && draft.variables?.id === r.id ? 'Starting…' : 'New draft'}
                    </Button>
                  )}
                </div>
              </div>
              {msg && msg.recipeId === r.id && (
                <div style={{ marginTop: 12 }}>
                  <Notice kind={msg.kind}>{msg.text}</Notice>
                </div>
              )}
              {printError?.key === r.id && (
                <div style={{ marginTop: 12 }}>
                  <Notice kind="error">{printError.text}</Notice>
                </div>
              )}
              {main && (main.lines?.length ?? 0) > 0 && (
                <ScalePanel
                  recipe={r}
                  produced={produced}
                  choice={choice}
                  result={result}
                  costCents={(scaledPub ?? scaledDraft)?.costCents ?? null}
                  expandable={expandable}
                  style={style}
                  onChange={setChoice}
                />
              )}
              {pub && scaledPub && (
                <VersionBlock
                  recipe={r}
                  version={pub}
                  scaled={scaledPub}
                  showSaved={choice.showSaved}
                  words={words}
                  inside={inside}
                  ingredientsByName={ingredientsByName}
                />
              )}
              {dr &&
                scaledDraft &&
                (editing ? (
                  <DraftEditor
                    key={dr.id}
                    recipe={r}
                    version={dr}
                    ingredients={ingredients}
                    ingredientsById={ingredientsById}
                    ingredientsByName={ingredientsByName}
                    onClose={() => setEditingId(null)}
                    onDiscard={() => setDiscardTarget({ recipe: r, version: dr })}
                    onPublished={() => {
                      setEditingId(null);
                      setMsg({
                        recipeId: r.id,
                        kind: 'ok',
                        text: 'Saved and published. New orders use this version; past orders keep theirs.',
                      });
                    }}
                  />
                ) : (
                  <VersionBlock
                    recipe={r}
                    version={dr}
                    scaled={scaledDraft}
                    showSaved={choice.showSaved}
                    words={words}
                    inside={inside}
                    ingredientsByName={ingredientsByName}
                    actions={
                      <>
                        <Button
                          variant="secondary"
                          onClick={() => {
                            setMsg(null);
                            setEditingId(dr.id);
                          }}
                        >
                          Edit draft
                        </Button>
                        {can('recipes.manage') && (
                          <Button
                            variant="danger"
                            onClick={() => setDiscardTarget({ recipe: r, version: dr })}
                          >
                            Discard draft
                          </Button>
                        )}
                      </>
                    }
                  />
                ))}
              <StepsSection
                recipe={r}
                words={words}
                onSaved={(text) => setMsg({ recipeId: r.id, kind: 'ok', text })}
              />
              <div className="rc-foot">
                <span>
                  {versions.length === 1
                    ? '1 version'
                    : `Versions: ${versions
                        .map((v) => `${v.version} (${STATUS_WORD[v.status] ?? v.status})`)
                        .join(', ')}`}
                </span>
                <div className="rc-foot-actions">
                  <HistoryLink
                    entityType="recipes"
                    entityId={r.id}
                    name={r.name}
                    label="Recipe history"
                  />
                  {can('records.delete') && (
                    <Button
                      variant="ghost"
                      aria-expanded={r.can_delete === false ? blocked : undefined}
                      style={{ color: 'var(--crave-red)', padding: '0 10px' }}
                      onClick={() => {
                        if (r.can_delete === false) setBlockedId(blocked ? null : r.id);
                        else setDeleteTarget(r);
                      }}
                    >
                      Delete recipe
                    </Button>
                  )}
                </div>
              </div>
              {blocked && (
                <div className="rc-blocked" role="status">
                  <strong>This recipe can't be deleted.</strong>
                  <span>
                    {r.delete_blocked_reason ??
                      'It is still in use. Keep it; only drafts can be discarded.'}
                  </span>
                  <div className="rc-actions">
                    {dr && can('recipes.manage') && (
                      <Button
                        variant="secondary"
                        onClick={() => setDiscardTarget({ recipe: r, version: dr })}
                      >
                        Discard draft version {dr.version} instead
                      </Button>
                    )}
                    {/* Retiring the menu item only helps when nothing else (past orders, tastings) uses it. */}
                    {r.uses?.['active_menu_item'] &&
                    !Object.entries(r.uses).some(
                      ([k, n]) => k !== 'active_menu_item' && Number(n) > 0,
                    ) &&
                    can('locations.manage') ? (
                      <Link className="rc-button-link" to="/readiness">
                        Open Menu, prices & hours
                      </Link>
                    ) : null}
                    <Button variant="ghost" onClick={() => setBlockedId(null)}>
                      OK
                    </Button>
                  </div>
                </div>
              )}
            </Card>
          );
        })}
      </div>

      <ConfirmDelete
        open={discardTarget !== null}
        onClose={() => setDiscardTarget(null)}
        permission="recipes.manage"
        title={
          discardTarget
            ? `Discard draft version ${discardTarget.version.version} of ${discardTarget.recipe.name}?`
            : 'Discard draft?'
        }
        description={
          discardTarget
            ? `The draft and its ${discardTarget.version.lines?.length ?? 0} ingredient ${
                discardTarget.version.lines?.length === 1 ? 'line are' : 'lines are'
              } removed, including changes not saved yet. ${
                (discardTarget.recipe.versions ?? []).some((v) => v.status === 'published')
                  ? 'The live version stays exactly as it is.'
                  : 'This recipe has no live version yet.'
              }`
            : undefined
        }
        actionLabel="Discard draft"
        pendingLabel="Discarding…"
        onConfirm={async () => {
          if (discardTarget) await discard(discardTarget.recipe, discardTarget.version);
        }}
      />

      <ConfirmDelete
        open={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        title={deleteTarget ? `Delete ${deleteTarget.name}?` : 'Delete recipe?'}
        description={
          deleteTarget
            ? `Deleting removes a recipe for good; the audit trail keeps a copy.${
                deleteTarget.produces_name
                  ? ` The ingredient ${deleteTarget.produces_name} stays on the Ingredients page.`
                  : ''
              }`
            : undefined
        }
        consequences={
          deleteTarget
            ? [
                deleteVersions.length === 1 ? '1 version' : `${deleteVersions.length} versions`,
                deleteLines === 1 ? '1 ingredient line' : `${deleteLines} ingredient lines`,
              ]
            : undefined
        }
        askReason
        onConfirm={async ({ reason }) => {
          if (!deleteTarget) return;
          const target = deleteTarget;
          const query = reason ? `?${new URLSearchParams({ reason }).toString()}` : '';
          await api.delete(`/admin/recipes/${target.id}${query}`);
          if ((target.versions ?? []).some((v) => v.id === editingId)) setEditingId(null);
          setBlockedId(null);
          setMsg({ recipeId: null, kind: 'ok', text: `Deleted ${target.name}.` });
          await Promise.all([refresh(), qc.invalidateQueries({ queryKey: ['ingredients'] })]);
        }}
        alternative={
          deleteTarget && deleteDraft
            ? {
                label: `Discard draft version ${deleteDraft.version} instead`,
                description: 'Only a draft can be discarded. The live version stays.',
                pendingLabel: 'Discarding…',
                onClick: () => discard(deleteTarget, deleteDraft),
              }
            : undefined
        }
      />
    </Section>
  );
}
